import React from 'react';
import Spacer from './Spacer';
import Text from './Typography/Text';

export interface FieldBlockProps {
    label?: string;
    /** Sits beside the label — a count, a status. */
    badge?: React.ReactNode;
    /** Sits at the far end of the label row — a copy button, a link. */
    action?: React.ReactNode;
    helperText?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

// `{cond && <Badge/>}` yields `false`, and `items.map(...)` over an empty list yields `[]`.
// React paints both as nothing; counted as present they open an empty label row. `0` is real.
const present = (node: React.ReactNode): boolean => {
    if (Array.isArray(node)) {
        return node.some(present);
    }

    return node !== undefined && node !== null && node !== false && node !== true && node !== '';
};

/**
 * Something a form has to show but does not ask for — a computed list, a generated value —
 * wearing a field's anatomy: label, content, helper text, on a field's metrics.
 */
export const FieldBlock: React.FC<FieldBlockProps> = ({
    label,
    badge,
    action,
    helperText,
    children,
    className = '',
    style,
}) => (
    <div className={className} style={style}>
        <Spacer direction="vertical" size={6} align="stretch" fullWidth>
            {(present(label) || present(badge) || present(action)) && (
                <div className="flex items-center gap-1.5 px-2">
                    {present(label) && (
                        <Text size="xsmall" color="primary" fontWeight="semi-bold">
                            {label}
                        </Text>
                    )}
                    {present(badge) && <span className="flex items-center">{badge}</span>}
                    {present(action) && <span className="ml-auto flex">{action}</span>}
                </div>
            )}
            <div className="px-2">{children}</div>
            {present(helperText) && (
                <div className="px-2">
                    <Text size="small" color="dimmed">
                        {helperText}
                    </Text>
                </div>
            )}
        </Spacer>
    </div>
);

export default FieldBlock;
